import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import "./ProfileAvatar.css";

const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiSuffix = (base) => {
  const clean = normalizeBase(base);
  if (!clean) return "";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiSuffix(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

const AUTH_TOKEN_KEYS = ["token", "authToken", "accessToken"];

const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && String(value).trim()) return String(value).trim();
  }

  return "";
};

const parseStoredUser = () => {
  try {
    const storedUser =
      localStorage.getItem("user") || sessionStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : {};
  } catch {
    return {};
  }
};

const normalizeProfile = (userData = {}) => {
  const parsedUser = parseStoredUser();

  const fallbackName =
    localStorage.getItem("fullName") ||
    sessionStorage.getItem("fullName") ||
    localStorage.getItem("name") ||
    sessionStorage.getItem("name") ||
    localStorage.getItem("userName") ||
    sessionStorage.getItem("userName") ||
    localStorage.getItem("username") ||
    sessionStorage.getItem("username") ||
    "";

  const fallbackEmail =
    localStorage.getItem("email") ||
    sessionStorage.getItem("email") ||
    localStorage.getItem("userEmail") ||
    sessionStorage.getItem("userEmail") ||
    localStorage.getItem("user_email") ||
    sessionStorage.getItem("user_email") ||
    localStorage.getItem("demoUserEmail") ||
    sessionStorage.getItem("demoUserEmail") ||
    "";

  const fullName =
    userData?.fullName ||
    userData?.full_name ||
    userData?.name ||
    parsedUser?.fullName ||
    parsedUser?.full_name ||
    parsedUser?.name ||
    parsedUser?.realName ||
    parsedUser?.username ||
    parsedUser?.displayName ||
    fallbackName ||
    "User";

  const email = userData?.email || parsedUser?.email || fallbackEmail || "";

  const avatarImage =
    userData?.profileImage ||
    userData?.profilePhoto ||
    userData?.photoURL ||
    userData?.avatarImage ||
    userData?.avatar ||
    userData?.image ||
    parsedUser?.profileImage ||
    parsedUser?.profilePhoto ||
    parsedUser?.photoURL ||
    parsedUser?.avatarImage ||
    parsedUser?.avatar ||
    parsedUser?.image ||
    "";

  return {
    fullName: String(fullName || "User").trim(),
    email: String(email || "").trim().toLowerCase(),
    avatarImage: String(avatarImage || "").trim(),
  };
};

function ProfileAvatar({ userData = {}, onLogout }) {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(() => normalizeProfile(userData));

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const syncProfileFromBackend = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setProfile(normalizeProfile(userData));
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/auth/me`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        setProfile(normalizeProfile(userData));
        return;
      }

      if (!response.ok || data?.success === false) {
        setProfile(normalizeProfile(userData));
        return;
      }

      const apiUser = data?.data || data?.user || {};
      const nextProfile = normalizeProfile({
        ...userData,
        ...apiUser,
      });

      setProfile(nextProfile);

      if (Object.keys(apiUser).length > 0) {
        localStorage.setItem("user", JSON.stringify(apiUser));

        if (apiUser.fullName || apiUser.full_name || apiUser.name) {
          localStorage.setItem(
            "fullName",
            apiUser.fullName || apiUser.full_name || apiUser.name
          );
        }

        if (apiUser.email) {
          localStorage.setItem("email", apiUser.email);
        }
      }
    } catch {
      setProfile(normalizeProfile(userData));
    }
  }, [userData]);

  useEffect(() => {
    syncProfileFromBackend();
  }, [syncProfileFromBackend]);

  useEffect(() => {
    const syncProfile = () => {
      syncProfileFromBackend();
    };

    window.addEventListener("storage", syncProfile);
    window.addEventListener("auth-state-changed", syncProfile);

    return () => {
      window.removeEventListener("storage", syncProfile);
      window.removeEventListener("auth-state-changed", syncProfile);
    };
  }, [syncProfileFromBackend]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  const fullName = profile.fullName || "User";
  const userEmail = profile.email || "";
  const avatarImage = profile.avatarImage || "";

  const avatarLetter = useMemo(() => {
    if (fullName && fullName !== "User") {
      return fullName.charAt(0).toUpperCase();
    }

    if (userEmail) {
      return userEmail.charAt(0).toUpperCase();
    }

    return "U";
  }, [fullName, userEmail]);

  const handleNavigate = (path) => {
    setOpen(false);
    navigate(path);
  };

  const handleLogoutClick = () => {
    setOpen(false);

    if (typeof onLogout === "function") {
      onLogout();
      return;
    }

    navigate("/signin", { replace: true });
  };

  const handleImageError = () => {
    setProfile((prev) => ({
      ...prev,
      avatarImage: "",
    }));
  };

  return (
    <div className="avatar-wrapper" ref={dropdownRef}>
      <button
        type="button"
        className="avatar-btn"
        onClick={() => setOpen((prev) => !prev)}
        aria-label="Open profile menu"
        aria-expanded={open}
      >
        {avatarImage ? (
          <img
            src={avatarImage}
            alt={`${fullName} profile`}
            onError={handleImageError}
          />
        ) : (
          <span>{avatarLetter}</span>
        )}
      </button>

      {open && (
        <div className="avatar-dropdown">
          <div className="avatar-user-info">
            <div className="avatar-name">{fullName}</div>
            {userEmail ? <div className="avatar-email">{userEmail}</div> : null}
          </div>

          <div className="avatar-divider" />

          <button type="button" onClick={() => handleNavigate("/profile")}>
            👤 Profile
          </button>

          <button type="button" onClick={() => handleNavigate("/payment-history")}>
            💳 Payment History
          </button>

          <button type="button" onClick={() => handleNavigate("/settings")}>
            ⚙️ Settings
          </button>

          <div className="avatar-divider" />

          <button
            type="button"
            className="avatar-logout-btn"
            onClick={handleLogoutClick}
          >
            🚪 Logout
          </button>
        </div>
      )}
    </div>
  );
}

ProfileAvatar.propTypes = {
  userData: PropTypes.shape({
    name: PropTypes.string,
    fullName: PropTypes.string,
    full_name: PropTypes.string,
    email: PropTypes.string,
    profileImage: PropTypes.string,
    profilePhoto: PropTypes.string,
    photoURL: PropTypes.string,
    avatarImage: PropTypes.string,
    avatar: PropTypes.string,
    image: PropTypes.string,
  }),
  onLogout: PropTypes.func,
};

ProfileAvatar.defaultProps = {
  userData: {},
  onLogout: null,
};

export default ProfileAvatar;